import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";

const DateRangeFilter = forwardRef(({ onChange }, ref) => {
    const [activeIndex, setActiveIndex] = useState("all");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const isMounted = useRef(false);

    const filterButton = [
        { label: "All", value: "all" },
        { label: "Today", value: "today" },
        { label: "Yesterday", value: "yesterday" },
        { label: "1W", value: "1w" },
        { label: "1M", value: "1m" },
        { label: "3M", value: "3m" },
        { label: "6M", value: "6m" },
        { label: "1Y", value: "1y" }
    ];

    // Expose reset method via ref
    useImperativeHandle(ref, () => ({
        reset: () => {
            setActiveIndex("all");
            setFromDate("");
            setToDate("");
            onChange?.({
                type: "range",
                value: "all",
                fromDate: "",
                toDate: "",
                key: false
            });
        }
    }));

    //DEFAULT "ALL" — ONLY ONCE
    useEffect(() => {
        onChange?.({
            type: "range",
            value: "all",
            fromDate: "",
            toDate: "",
            key: false
        });
        isMounted.current = true;
    }, []);

    //Quick Button Click
    const handleFilterClick = (btn) => {
        setActiveIndex(btn.value);
        setFromDate("");
        setToDate("");

        onChange?.({
            type: "range",
            value: btn.value,
            fromDate: "",
            toDate: "",
            key: true
        });
    };

    // Custom Range Trigger — ONLY when BOTH dates filled
    useEffect(() => {
        if (!isMounted.current) return;
        if (!fromDate && !toDate) return;

        setActiveIndex("custom");

        onChange?.({
            type: "custom",
            value: "",
            fromDate,
            toDate,
            key: true
        });
    }, [fromDate, toDate]);

    return (
        <div className="space-y-5">
            {/* PREMIUM QUICK FILTER BUTTONS */}
            <div className="flex gap-2 flex-wrap items-center justify-between">
                <div className="flex gap-2 flex-wrap">
                    {filterButton.map((btn) => (
                        <button
                            key={btn.value}
                            onClick={() => handleFilterClick(btn)}
                            className={`relative px-4 py-1.5 text-xs font-bold rounded-lg transition-all duration-300
                          ${activeIndex === btn.value
                                    ? "bg-gradient-to-r from-blue-500 to-violet-600 text-white shadow-lg scale-105 ring-2 ring-blue-200"
                                    : "bg-white text-slate-700 border border-slate-300 hover:bg-blue-50 hover:text-blue-600"
                                }`}
                        >
                            {btn.label}

                            {activeIndex === btn.value && (
                                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full 
                                bg-gradient-to-br from-blue-200 via-blue-500 to-blue-800
                                shadow-[inset_0_1px_2px_rgba(255,255,255,0.7),0_3px_6px_rgba(0,0,0,0.4)]
                                border border-white"
                                />
                            )}
                        </button>
                    ))}
                </div>
                {/*PREMIUM CUSTOM DATE RANGE UI */}
                <div className="flex flex-wrap items-center gap-4 ">
                    {/* FROM DATE */}
                    <div>
                        <label className="block mb-1 text-xs font-semibold text-slate-600">
                            From Date
                        </label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="px-3 py-1.5 text-xs border-2 border-slate-200 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition"
                        />
                    </div>

                    {/* ARROW ICON */}
                    <div className="pt-5 hidden sm:block">
                        <svg
                            className="w-6 h-6 text-slate-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13 7l5 5m0 0l-5 5m5-5H6"
                            />
                        </svg>
                    </div>

                    {/* TO DATE */}
                    <div>
                        <label className="block mb-1 text-xs font-semibold text-slate-600">
                            To Date
                        </label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="px-3 py-1.5 text-xs border-2 border-slate-200 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
});

export default DateRangeFilter;
